import { Shield } from 'lucide-react';
import { EntityForm } from '@components/integrated/entity-form';
import type { EntityFormConfig } from '@components/integrated/entity-form';
import { useAdminAuthStore } from '../auth/store';
import type { AdminLoginResponse } from '../auth/types';
import { Card, VStack, HStack } from '@components/base/layout';
import { Text, Icon } from '@components/base/display';

const TOKEN_KEY = 'admin_auth_token';
const TENANT_KEY = 'admin_active_tenant_id';

const loginFormConfig: EntityFormConfig = {
  id: 'admin-login',
  endpoint: {
    erpEndpoint: '/api/admin/auth/login',
    method: 'POST',
  },
  fields: {
    username: {
      type: 'text',
      label: 'Username',
      placeholder: 'Enter your username',
      order: 1,
      validation: { required: true },
    },
    password: {
      type: 'password',
      label: 'Password',
      placeholder: 'Enter your password',
      order: 2,
      validation: { required: true },
    },
  },
  actions: {
    submit: {
      label: 'Sign in',
      variant: 'primary',
      className: 'w-full',
    },
    cancel: { visible: false },
    reset: { visible: false },
  },
  workflow: {
    onError: {
      mapFieldErrors: true,
    },
  },
  layout: {
    columns: 1,
    gap: 'md',
  },
};

export function LoginPage() {
  const handleSuccess = (response: unknown) => {
    const loginResponse = response as AdminLoginResponse;
    const { token, user } = loginResponse.data;
    const tenants = user.tenants ?? [];
    localStorage.setItem(TOKEN_KEY, token);
    const persisted = localStorage.getItem(TENANT_KEY);
    const firstTenant = tenants[0];
    const activeTenantId =
      persisted ?? (firstTenant ? String(firstTenant.id) : null);
    if (activeTenantId) {
      localStorage.setItem(TENANT_KEY, activeTenantId);
    }
    useAdminAuthStore.setState({
      token,
      user,
      tenants,
      activeTenantId,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  };

  return (
    <div className="min-h-screen bg-secondary-50 flex items-center justify-center p-4">
      <Card variant="elevated" padding="lg" className="w-full max-w-md">
        <Card.Content>
          <VStack gap={6}>
            <VStack gap={2} align="center">
              <HStack gap={2} align="center">
                <Icon icon={Shield} size="lg" color="primary" />
                <Text as="h1" size="xl" weight="bold" color="primary">
                  Origami Admin
                </Text>
              </HStack>
              <Text size="sm" color="muted">
                Sign in to access the administration panel
              </Text>
            </VStack>

            <EntityForm config={loginFormConfig} onSuccess={handleSuccess}>
              <EntityForm.Alert />
              <EntityForm.Fields />
              <EntityForm.Actions />
            </EntityForm>
          </VStack>
        </Card.Content>
      </Card>
    </div>
  );
}
